import React, { useState, useEffect } from 'react';
import { Bell, Clock, AlertTriangle, BarChart3, Save, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

/**
 * AdminUrgentNotificationSettings Component
 * 
 * Admin settings panel for urgent job notifications:
 * - Notification delay for free users
 * - Enable/disable notifications
 * - Enable/disable missed job alerts
 */
export default function AdminUrgentNotificationSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    pro_delay_minutes: 10,
    missed_job_alerts_enabled: true,
    show_missed_jobs_count_days: 7,
    max_notifications_per_hour: 10,
    notification_cooldown_minutes: 5
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/urgent-notifications/settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/urgent-notifications/stats`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/urgent-notifications/settings`,
        settings,
        { withCredentials: true }
      );
      toast.success('Notification settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="h-32 bg-slate-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="urgent-notification-settings">
      <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
        <Bell className="h-5 w-5 text-orange-500" />
        Urgent Job Notifications
      </h2>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{stats.today?.total || 0}</p>
            <p className="text-xs text-blue-600">Today's Notifications</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-amber-700">{stats.today?.pro || 0}</p>
            <p className="text-xs text-amber-600">Pro (Instant)</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-slate-700">{stats.today?.free || 0}</p>
            <p className="text-xs text-slate-600">Free (Delayed)</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-red-700">{stats.this_week?.missed_jobs || 0}</p>
            <p className="text-xs text-red-600">Missed Jobs (Week)</p>
          </div>
        </div>
      )}

      {/* Weekly Performance */}
      {stats?.this_week && (
        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
          <h3 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            This Week Performance
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-600">Total Notifications:</span>
              <span className="ml-2 font-medium">{stats.this_week.total}</span>
            </div>
            <div>
              <span className="text-slate-600">Click Rate:</span>
              <span className="ml-2 font-medium">{stats.this_week.click_rate}%</span>
            </div>
            <div>
              <span className="text-slate-600">Pro Click Rate:</span>
              <span className="ml-2 font-medium text-amber-600">{stats.this_week.pro_click_rate}%</span>
            </div>
            <div>
              <span className="text-slate-600">Pro vs Free:</span>
              <span className="ml-2 font-medium">{stats.this_week.pro} / {stats.this_week.free}</span>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="space-y-5">
        {/* Enable Notifications */}
        <div className="flex items-center justify-between py-3 border-b border-slate-100">
          <div>
            <p className="font-medium text-slate-900">Enable Urgent Notifications</p>
            <p className="text-xs text-slate-500">Send push notifications for urgent jobs</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`p-2 rounded-lg transition-colors ${
              settings.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
            }`}
            data-testid="urgent-notifications-toggle"
          >
            {settings.enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
          </button>
        </div>

        {/* Pro Delay */}
        <div className="py-3 border-b border-slate-100">
          <label className="block font-medium text-slate-900 mb-1 flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            Free User Delay (minutes)
          </label>
          <p className="text-xs text-slate-500 mb-2">
            How long after Pro users before free users see jobs
          </p>
          <input
            type="number"
            value={settings.pro_delay_minutes}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              pro_delay_minutes: parseInt(e.target.value) || 10 
            }))}
            min={1}
            max={60}
            className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            data-testid="pro-delay-input"
          />
          <span className="ml-2 text-sm text-slate-500">minutes (default: 10)</span>
        </div>

        {/* Missed Job Alerts */}
        <div className="flex items-center justify-between py-3 border-b border-slate-100">
          <div>
            <p className="font-medium text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Missed Job Alerts
            </p>
            <p className="text-xs text-slate-500">Show "You missed X jobs" alerts to free users</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ 
              ...prev, 
              missed_job_alerts_enabled: !prev.missed_job_alerts_enabled 
            }))}
            className={`p-2 rounded-lg transition-colors ${
              settings.missed_job_alerts_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
            }`}
            data-testid="missed-alerts-toggle"
          >
            {settings.missed_job_alerts_enabled ? (
              <ToggleRight className="h-6 w-6" />
            ) : (
              <ToggleLeft className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Missed Jobs Window */}
        <div className="py-3 border-b border-slate-100">
          <label className="block font-medium text-slate-900 mb-1">
            Missed Jobs Display Window
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Show missed jobs from the last X days
          </p>
          <select
            value={settings.show_missed_jobs_count_days}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              show_missed_jobs_count_days: parseInt(e.target.value) 
            }))}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            data-testid="missed-jobs-window-select"
          >
            <option value={3}>3 days</option>
            <option value={7}>7 days (recommended)</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>

        {/* Rate Limiting */}
        <div className="py-3">
          <label className="block font-medium text-slate-900 mb-1">
            Max Notifications Per Hour
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Prevent notification spam to individual users
          </p>
          <input
            type="number"
            value={settings.max_notifications_per_hour}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              max_notifications_per_hour: parseInt(e.target.value) || 10 
            }))}
            min={1}
            max={50}
            className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            data-testid="max-notifications-input"
          />
          <span className="ml-2 text-sm text-slate-500">per user per hour</span>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50"
        data-testid="save-notification-settings-btn"
      >
        {saving ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="h-5 w-5" />
            Save Notification Settings
          </>
        )}
      </button>
    </div>
  );
}
